import React, { useEffect, useRef, useState } from 'react';
import { createHtmlTemplate } from '../api/TemplateService';
import { sendHtmlEmail } from '../api/emailapi.js';
import { 
  FaMapMarkerAlt, 
  FaPhoneAlt, 
  FaEnvelope, 
  FaClock,
  FaWhatsapp,
  FaPaperPlane,
  FaUser,
  FaComment,
  FaCheckCircle,
  FaStar,
  FaArrowRight,
  FaCheck,
  FaRegCheckSquare,
  FaChevronDown
} from 'react-icons/fa';
import { GiCarKey, GiMechanicGarage } from 'react-icons/gi';

const ContactUs = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    agreeTerms: false,
    receiveUpdates: false
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (headerRef.current) observer.observe(headerRef.current);
    if (leftRef.current) observer.observe(leftRef.current);
    if (rightRef.current) observer.observe(rightRef.current);

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const templateData = {
      fullName: formData.name,
      email: formData.email,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message,
      agreeTerms: formData.agreeTerms,
      receiveUpdates: formData.receiveUpdates
    };
    
    const generatedHtml = createHtmlTemplate(templateData);
    
    try {
      const result = await sendHtmlEmail(
        "info@ambrisservice.com", 
        "New Lead Alert - Contact Us", 
        generatedHtml
      );
      
      if (result.success) {
        console.log("Email dispatched successfully.");
      }
    } catch (err) {
      console.error("Failed to send lead:", err);
    }
    
    setIsSubmitting(false);
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 3000);
    setFormData({ 
      name: '', 
      email: '', 
      phone: '', 
      subject: '',
      message: '',
      agreeTerms: false,
      receiveUpdates: false
    });
  };

  const contactInfo = [
    {
      id: 1,
      title: 'Address',
      icon: <FaMapMarkerAlt />,
      details: ['Sathawa, Sarnath, Varanasi', 'Uttar Pradesh - 221007'],
      color: 'bg-red-50 text-red-500'
    },
    {
      id: 2,
      title: 'Call Us',
      icon: <FaPhoneAlt />,
      details: ['+91 8081321406', '+91 9161985614'],
      color: 'bg-blue-50 text-blue-500'
    },
    {
      id: 3,
      title: 'Working Hours',
      icon: <FaClock />,
      details: ['Monday – Sunday', '9:00 AM – 7:00 PM'],
      color: 'bg-green-50 text-green-500'
    },
    {
      id: 4,
      title: 'WhatsApp',
      icon: <FaWhatsapp />,
      details: ['+91 8081321406', 'Quick response within 1 hour'],
      color: 'bg-emerald-50 text-emerald-500'
    }
  ];

  const subjectOptions = [
    'Select a subject',
    'General Inquiry',
    'Service Booking',
    'Car Repair',
    'Bike Service',
    'Spare Parts',
    'Complaint',
    'Feedback'
  ];

  return (
    <section ref={sectionRef} className="py-10 md:py-16 bg-gradient-to-b from-green-50 to-white">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-12 opacity-0">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ambris <span className="text-red-600">Service Center</span>
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-lg">
            Get in Touch with Us
          </p>
          <div className="w-20 h-0.5 bg-red-500 mx-auto mt-6"></div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
          {/* Left Side - Contact Info */}
          <div ref={leftRef} className="w-full lg:w-1/2 opacity-0">
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden h-full">
              <div className="bg-red-600 px-6 py-5">
                <h3 className="text-white font-bold text-xl">Contact Information</h3>
                <p className="text-red-100 text-sm mt-1">We'd love to hear from you</p>
              </div>
              
              <div className="p-6 space-y-5">
                {contactInfo.map((info) => (
                  <div key={info.id} className="flex items-start gap-4 group">
                    <div className={`w-12 h-12 ${info.color} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <div className="text-xl">{info.icon}</div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-800">{info.title}</h4>
                      {info.details.map((detail, idx) => (
                        <p key={idx} className="text-gray-500 text-sm">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="m-6 mt-0 bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                <div className="flex items-center gap-3">
                  <FaWhatsapp className="text-green-500 text-xl" />
                  <div>
                    <p className="text-gray-700 text-sm font-medium">For quick service and inquiries</p>
                    <p className="text-gray-500 text-xs">Feel free to call or WhatsApp us anytime</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Contact Form with Option Tick */}
          <div ref={rightRef} className="w-full lg:w-1/2 opacity-0">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 h-full">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Send us a Message</h3>
                <p className="text-gray-500 text-sm mt-1">We'll get back to you within 24 hours</p>
              </div>

              {formSubmitted ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaCheckCircle className="text-green-500 text-3xl" />
                  </div>
                  <h4 className="text-green-700 font-bold text-lg">Enquiry Sent!</h4>
                  <p className="text-green-600 text-sm mt-2">Thank you for contacting us. We'll reach out to you soon.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 transition-all"
                        placeholder="Enter your full name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Email Address <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 transition-all"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm font-medium mb-2">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <FaPhoneAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 transition-all"
                          placeholder="+91 8081321406"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Select Subject with custom dropdown styling - Option Tick */}
                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Select Subject <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 transition-all appearance-none bg-white cursor-pointer"
                      >
                        {subjectOptions.map((option, idx) => (
                          <option key={idx} value={option === 'Select a subject' ? '' : option}>
                            {option}
                          </option>
                        ))}
                      </select>
                      <FaChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 text-sm font-medium mb-2">
                      Your Message <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <FaComment className="absolute left-3 top-4 text-gray-400 text-sm" />
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="4"
                        className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 transition-all resize-none"
                        placeholder="Tell us about your vehicle or service requirement..."
                      ></textarea>
                    </div>
                  </div>

                  {/* Option Tick Checkboxes - Terms & Conditions */}
                  <div className="space-y-3">
                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        name="agreeTerms"
                        checked={formData.agreeTerms}
                        onChange={handleChange}
                        required
                        className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-200 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700 text-sm group-hover:text-gray-900 transition">
                        I agree to the <span className="text-red-600 font-medium">Terms & Conditions</span> and <span className="text-red-600 font-medium">Privacy Policy</span>.
                      </span>
                    </label>

                    <label className="flex items-center cursor-pointer group">
                      <input
                        type="checkbox"
                        name="receiveUpdates"
                        checked={formData.receiveUpdates}
                        onChange={handleChange}
                        className="w-5 h-5 rounded border-gray-300 text-red-600 focus:ring-red-200 focus:ring-2 cursor-pointer"
                      />
                      <span className="ml-3 text-gray-700 text-sm group-hover:text-gray-900 transition">
                        I would like to receive updates from Ambris Service Center via Email, SMS, WhatsApp, and RCS.
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-red-200 disabled:bg-red-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Sending..." : "SEND ENQUIRY"}
                    {!isSubmitting && <FaPaperPlane className="text-sm" />}
                  </button>

                  <p className="text-gray-400 text-xs text-center flex items-center justify-center gap-1">
                    <i className="fas fa-shield-alt text-green-500"></i>
                    Your information is safe with us
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-12 opacity-0 animate-fade-up" style={{ animationDelay: '0.2s' }}>
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-red-600 px-6 py-4">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <FaMapMarkerAlt />
                Find Us On Map
              </h3>
              <p className="text-red-100 text-sm mt-1">Ambris Bike & Car Service Center, Sathawa, Sarnath, Varanasi</p>
            </div>
            <div className="h-80 md:h-96 w-full">
              <iframe
                title="Ambris Service Center Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d14425.571565205274!2d82.958665!3d25.317645!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x398e2d9b1f9f9f9f%3A0x9f9f9f9f9f9f9f9f!2sSarnath%2C%20Varanasi%2C%20Uttar%20Pradesh%20221007!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="hover:grayscale-0 transition-all duration-500"
              ></iframe>
            </div>
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-100">
              <p className="text-gray-600 text-sm flex items-center gap-2">
                <FaMapMarkerAlt className="text-red-500 text-xs" />
                Sathawa, Sarnath, Varanasi, Uttar Pradesh - 221007
              </p>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-10 flex flex-wrap justify-center gap-8">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">Quick Response</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">Free Consultation</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">No Hidden Charges</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-500 text-sm">100% Satisfaction</span>
          </div>
        </div>
      </div>

      {/* WhatsApp Float Button */}
      <a
        href="https://wa.me/918081321406"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110 z-50"
      >
        <FaWhatsapp className="text-2xl" />
      </a>

      <style jsx>{`
        @keyframes fadeUp {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-up {
          animation: fadeUp 0.6s ease-out forwards;
        }
        input[type="checkbox"] {
          accent-color: #dc2626;
        }
      `}</style>
    </section>
  );
};

export default ContactUs;